/*
    The audiobook player, shared by the whole site (HAP-4591).

    One <audio> element lives here, outside any page, so a chapter keeps
    playing while the listener moves around the site, the way a music app
    does. /audiobooks/:slug is the library (components/pages/audiobook.vue),
    components/audiobook/playerBar.vue the controls at the foot of every page,
    and nowPlaying.vue the full-screen player. Built after the Glory of the
    Cross player, which this follows closely.

    Each book is a JSON file in data/audiobooks/ written by
    scripts/audiobook/<slug>.py; the mp3s stream from S3.

    Saved on this device, per book: each track's place, which tracks were
    finished, the last track, and the speed.
*/
import { reactive } from 'vue'

const WIAF = {
    cover: 'https://cfl-mango.s3.us-east-1.amazonaws.com/assets/products/6973b861ab5aa8835b29a543/image.jpg',
    product: '/store/6973b861ab5aa8835b29a543',
    // HAP-4591: the same narration script in two voice/model pairings, to compare
    editions: [
        { slug: 'what-is-a-family', label: 'Professional voice · v2' },
        { slug: 'what-is-a-family-v4', label: 'Instant clone · v4' },
        { slug: 'what-is-a-family-pro-v4', label: 'Professional voice · v4 (sample)' },
    ],
    note: 'The Scripture references listed after each answer are left out of the reading; you\'ll find them in the book.',
}
const TFTS = {
    cover: 'https://cfl-mango.s3.us-east-1.amazonaws.com/assets/products/69b30dbf6a8daf78584b4538/image.jpg',
    product: '/store/69b30dbf6a8daf78584b4538',
    theme: 'ab-theme-tfts',
    note: 'Scripture references in parentheses and the footnotes are left out of the reading; you\'ll find them in the book.',
    // HAP-4591: the instant clone (first edition) vs the professional voice once it could be trained on v4
    editions: [
        { slug: 'the-family-that-sings', label: 'Instant clone · v4' },
        { slug: 'the-family-that-sings-pro', label: 'Professional voice · v4' },
    ],
}
export const BOOKS = {
    'what-is-a-family': { ...WIAF, load: () => import('../data/audiobooks/what-is-a-family.json') },
    'what-is-a-family-v4': { ...WIAF, load: () => import('../data/audiobooks/what-is-a-family-v4.json') },
    'what-is-a-family-pro-v4': { ...WIAF, load: () => import('../data/audiobooks/what-is-a-family-pro-v4.json') },
    // eleven_v4 in the instant clone and in the professional voice, in the book's own colours (theme: audiobook.css)
    'the-family-that-sings': { ...TFTS, load: () => import('../data/audiobooks/the-family-that-sings.json') },
    'the-family-that-sings-pro': { ...TFTS, load: () => import('../data/audiobooks/the-family-that-sings-pro.json') },
}

const KEY = (slug, k) => `cfl:audiobook:${slug}:${k}`
export const RATES = [0.75, 1, 1.25, 1.5, 1.75, 2]
export const SLEEP_OPTIONS = [15, 30, 45, 60, 'end']
const LAST_BOOK = 'cfl:audiobook:last-book'

const read = (k, fallback) => {
    try { return JSON.parse(localStorage.getItem(k)) ?? fallback } catch (e) { return fallback }
}

export const player = reactive({
    slug: null,
    book: null, // { title, subtitle, author, voice, tracks, cover, product }
    tracks: [],
    current: null, // track being played (or cued)
    playing: false,
    waiting: false, // buffering
    position: 0,
    duration: 0,
    rate: 1,
    saved: {}, // track number -> seconds listened
    done: {}, // track number -> true once finished
    sleep: null, // { minutes, endsAt } or { end: true }
    started: false, // pressed play this visit: the bar follows the listener around
    sheetOpen: false, // full-screen player
})

let audio = null
const loading = {}

export function loadBook(slug) {
    const def = BOOKS[slug]
    if (!def) return Promise.resolve(null)
    if (player.slug === slug && player.book) return Promise.resolve(player.book)
    loading[slug] ??= def.load().then((m) => m.default || m)
    return loading[slug].then((data) => {
        // Another book is playing: leave it alone until this one is played.
        if (player.slug && player.slug !== slug && player.playing) return { ...data, ...def, slug }
        useBook(slug, data)
        return player.book
    })
}

function useBook(slug, data) {
    if (player.slug === slug) return
    if (audio) audio.pause()
    player.slug = slug
    player.book = { ...data, ...BOOKS[slug], slug }
    player.tracks = data.tracks || []
    player.saved = {}
    for (const t of player.tracks) player.saved[t.number] = Number(localStorage.getItem(KEY(slug, `pos:${t.number}`))) || 0
    player.done = read(KEY(slug, 'done'), {})
    const rate = read(KEY(slug, 'rate'), 1)
    player.rate = RATES.includes(rate) ? rate : 1
    player.current = null
    localStorage.setItem(LAST_BOOK, slug)
    // Cue the last track (or the first) so Play has something to play.
    const last = player.tracks.find((t) => String(t.number) === localStorage.getItem(KEY(slug, 'last')))
    if (last || player.tracks[0]) cue(last || player.tracks[0])
}

/* Returning listeners: cue their book, so the bar is ready when they come back. */
export function resumeLastBook() {
    const slug = localStorage.getItem(LAST_BOOK)
    if (slug && BOOKS[slug]) loadBook(slug)
}

function el() {
    if (audio) return audio
    audio = new Audio()
    audio.preload = 'metadata'
    audio.addEventListener('timeupdate', () => {
        player.position = audio.currentTime
        if (Math.floor(audio.currentTime) % 5 === 0) save()
        if (player.sleep?.endsAt && Date.now() >= player.sleep.endsAt) {
            audio.pause()
            player.sleep = null
        }
    })
    audio.addEventListener('loadedmetadata', () => {
        if (isFinite(audio.duration)) player.duration = audio.duration
        const at = player.saved[player.current?.number] || 0
        if (at && at < audio.duration - 5) audio.currentTime = at
        audio.playbackRate = player.rate
    })
    audio.addEventListener('play', () => { player.playing = true; player.started = true; mediaState() })
    audio.addEventListener('pause', () => { player.playing = false; save(); mediaState() })
    audio.addEventListener('waiting', () => { player.waiting = true })
    audio.addEventListener('playing', () => { player.waiting = false })
    audio.addEventListener('canplay', () => { player.waiting = false })
    audio.addEventListener('ended', onEnded)
    // One voice at a time: a sermon, podcast or video started anywhere on the
    // site pauses the book (play doesn't bubble, so listen in the capture phase).
    document.addEventListener('play', (e) => { if (e.target !== audio && !audio.paused) audio.pause() }, true)
    return audio
}

function cue(t) {
    if (!t) return false
    save()
    player.current = t
    player.duration = t.duration
    player.position = player.saved[t.number] || 0
    const a = el()
    a.src = `${t.file}?v=${t.v || ''}`
    a.playbackRate = player.rate
    localStorage.setItem(KEY(player.slug, 'last'), String(t.number))
    mediaSession(t)
    return true
}

/* Play track t of the book on the page; if it's already the current one, play/pause it. */
export function play(t, slug = player.slug) {
    if (slug && slug !== player.slug) {
        return loading[slug]?.then((data) => { useBook(slug, data); play(player.tracks.find((x) => x.number === t?.number)) })
    }
    if (t && player.current?.number !== t.number) {
        if (!cue(t)) return
        // A finished track starts again from the top.
        if (player.done[t.number] && !player.saved[t.number]) markDone(t.number, false)
        el().play().catch(() => {})
        return
    }
    toggle()
}

export function toggle() {
    if (!player.current) return
    const a = el()
    if (a.paused) a.play().catch(() => {})
    else a.pause()
}

export function pause() {
    if (audio && !audio.paused) audio.pause()
}

/* Stop and put the bar away (the sermon mini-player is taking the foot of the page). */
export function dismiss() {
    pause()
    player.started = false
    player.sheetOpen = false
}

export function seek(seconds) {
    const a = el()
    const s = Math.min(Math.max(0, Number(seconds)), player.duration || a.duration || 0)
    a.currentTime = s
    player.position = s
}

export const skip = (s) => seek(el().currentTime + s)

export function neighbour(step) {
    if (!player.current) return null
    const i = player.tracks.findIndex((t) => t.number === player.current.number)
    return player.tracks[i + step] || null
}

/* Back goes to the start of the track first, like any player; twice goes to the previous one. */
export function previous() {
    if (el().currentTime > 5 || !neighbour(-1)) return seek(0)
    play(neighbour(-1))
}

export function next() {
    const n = neighbour(1)
    if (n) play(n)
}

export function setRate(r) {
    player.rate = r
    el().playbackRate = r
    localStorage.setItem(KEY(player.slug, 'rate'), JSON.stringify(r))
}

export function setSleep(option) {
    if (!option) player.sleep = null
    else if (option === 'end') player.sleep = { end: true }
    else player.sleep = { minutes: option, endsAt: Date.now() + option * 60000 }
}

function onEnded() {
    const n = player.current?.number
    if (n == null) return
    player.saved[n] = 0
    localStorage.removeItem(KEY(player.slug, `pos:${n}`))
    markDone(n, true)
    if (player.sleep?.end) { player.sleep = null; return }
    next() // on into the next chapter, like a book
}

function markDone(n, yes) {
    if (yes) player.done[n] = true
    else delete player.done[n]
    localStorage.setItem(KEY(player.slug, 'done'), JSON.stringify(player.done))
}

function save() {
    // a finished track has no place to save (onEnded cleared it)
    if (!player.current || !audio || audio.ended) return
    const n = player.current.number
    const at = Math.floor(audio.currentTime || 0)
    if (!at) return
    player.saved[n] = at
    localStorage.setItem(KEY(player.slug, `pos:${n}`), String(at))
}

/* Listened-to state of a track, for the library. */
export function progressOf(t) {
    const total = t?.duration || 0
    const n = t?.number
    const at = player.current?.number === n ? player.position : player.saved[n] || 0
    if (player.done[n] && !at) return { state: 'done', fraction: 1, left: 0 }
    if (at > 5) return { state: 'started', fraction: total ? at / total : 0, left: Math.max(0, total - at) }
    return { state: 'new', fraction: 0, left: total }
}

export function clock(s) {
    s = Math.max(0, Math.floor(s || 0))
    const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = String(s % 60).padStart(2, '0')
    return h ? `${h}:${String(m).padStart(2, '0')}:${sec}` : `${m}:${sec}`
}

/* "52 min", "1 hr 4 min" */
export function minutes(s) {
    const m = Math.round((s || 0) / 60)
    if (m < 60) return `${Math.max(1, m)} min`
    return `${Math.floor(m / 60)} hr${m % 60 ? ` ${m % 60} min` : ''}`
}

/* Lock-screen and headphone controls. */
function mediaSession(t) {
    if (!('mediaSession' in navigator) || !window.MediaMetadata) return
    navigator.mediaSession.metadata = new window.MediaMetadata({
        title: t.label === t.title ? t.title : `${t.label}: ${t.title}`,
        artist: player.book.author,
        album: player.book.title,
        artwork: [{ src: player.book.cover, sizes: '718x1113', type: 'image/jpeg' }],
    })
    const on = (a, f) => { try { navigator.mediaSession.setActionHandler(a, f) } catch (e) { /* unsupported */ } }
    on('play', () => el().play().catch(() => {}))
    on('pause', () => el().pause())
    on('seekbackward', () => skip(-15))
    on('seekforward', () => skip(30))
    on('seekto', (d) => seek(d.seekTime))
    on('previoustrack', () => previous())
    on('nexttrack', () => next())
}

function mediaState() {
    if ('mediaSession' in navigator) navigator.mediaSession.playbackState = player.playing ? 'playing' : 'paused'
}
